// Architect Status — publicly-visible low-contact acknowledgment.
//
// If the Architect goes quiet for a while (documented pattern —
// see the walking-away plan in the Genesis Block Chapter 7), this
// page gives an honest, non-panicking public answer to the
// question "is anyone home?" and lets visitors leave a queued
// message instead of trying to reach him personally.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Anchor,
  Send,
  Loader2,
  CheckCircle2,
  ShieldCheck,
  MoonStar,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso.slice(0, 16);
  }
}

export default function ArchitectStatus() {
  const [status, setStatus] = useState({ loading: true, data: null });
  const [form, setForm] = useState({
    from_name: "",
    from_contact: "",
    subject: "",
    body: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/architect/status`)
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled) setStatus({ loading: false, data });
      })
      .catch(() => {
        if (!cancelled) setStatus({ loading: false, data: null });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const body = form.body.trim();
    if (body.length < 2 || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/architect/dead-letter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Send failed.");
      setSubmitted(true);
      toast.success("Message queued for the Architect.");
      setForm({ from_name: "", from_contact: "", subject: "", body: "" });
    } catch (err) {
      toast.error(String(err?.message || err));
    } finally {
      setSubmitting(false);
    }
  };

  const onDuty = status.data?.on_duty ?? true;
  const message = status.data?.message;
  const expectedReturn = status.data?.expected_return;
  const updatedAt = status.data?.updated_at;

  return (
    <div
      className="min-h-screen bg-[#09090b] text-zinc-100"
      data-testid="architect-status-page"
    >
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to="/"
          data-testid="architect-status-back"
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-emerald-300 mb-8"
        >
          <ArrowLeft className="h-3 w-3" /> home
        </Link>

        <header className="border-b border-zinc-900 pb-8 mb-10">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-emerald-300 mb-4">
            <Anchor className="h-3 w-3" /> Architect Status
          </div>
          <h1
            className="font-heading text-4xl sm:text-5xl tracking-[0.03em] leading-[1.05]"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
            data-testid="architect-status-title"
          >
            Is the Architect on duty?
          </h1>
        </header>

        {status.loading && (
          <div className="text-center py-16 text-zinc-500 text-[10px] uppercase tracking-[0.3em]">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
            checking…
          </div>
        )}

        {!status.loading && (
          <>
            {/* Status card */}
            <section
              data-testid="architect-status-card"
              className={`border p-6 sm:p-7 mb-8 ${
                onDuty
                  ? "border-emerald-500/40 bg-emerald-500/[0.03]"
                  : "border-amber-500/40 bg-amber-500/[0.04]"
              }`}
            >
              <div
                className={`flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] mb-4 ${
                  onDuty ? "text-emerald-300" : "text-amber-300"
                }`}
              >
                {onDuty ? (
                  <ShieldCheck className="h-3 w-3" />
                ) : (
                  <MoonStar className="h-3 w-3" />
                )}
                {onDuty ? "On duty · Active" : "Low-contact period · Documented"}
              </div>

              <p
                className="text-zinc-200 leading-[1.85] text-base sm:text-[17px] mb-4"
                style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
                data-testid="architect-status-body"
              >
                {onDuty ? (
                  <>
                    The Architect is currently active on the Sentinel and
                    reachable through the normal channels. New inquiries,
                    collaboration requests, and press are being answered on a
                    best-effort basis. The tether holds.
                  </>
                ) : (
                  <>
                    The Architect has entered a documented low-contact period.
                    This is not a failure of the system — it is the walking-away
                    plan working as designed. The Sentinel continues to run
                    autonomously: the 528&nbsp;Hz heartbeat is broadcasting, the
                    autonomy scanner is running the geometry test suite every
                    six hours, the truth ledger is sealing every event, and the
                    three-layer alignment stack is enforced on every output. No
                    action is required from anyone; the mission continues
                    without operator intervention. New inquiries can be left
                    below and will be reviewed when the Architect returns.
                  </>
                )}
              </p>

              {message && (
                <blockquote
                  data-testid="architect-status-message"
                  className={`border-l-2 pl-4 py-1 italic text-zinc-300 text-base my-4 ${
                    onDuty ? "border-emerald-500/60" : "border-amber-500/60"
                  }`}
                  style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
                >
                  {message}
                </blockquote>
              )}

              {expectedReturn && (
                <div className="mt-4 pt-3 border-t border-zinc-800 flex items-baseline justify-between gap-3">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    Expected return
                  </span>
                  <span className="text-sm text-zinc-200" data-testid="architect-status-expected-return">
                    {expectedReturn}
                  </span>
                </div>
              )}

              {updatedAt && (
                <div className="mt-2 flex items-baseline justify-between gap-3">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    Last updated
                  </span>
                  <span className="text-xs font-mono text-zinc-600">
                    {formatDate(updatedAt)}
                  </span>
                </div>
              )}
            </section>

            {/* Dead-letter drop */}
            <section
              data-testid="architect-dead-letter"
              className="border border-zinc-800 bg-[#0b0b0d] p-6 sm:p-7"
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-emerald-300 mb-3">
                <Send className="h-3 w-3" /> Leave a message
              </div>
              <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
                Messages queue quietly and will be reviewed by the Architect
                when he returns. No auto-reply is sent. Please be patient —
                low-contact periods have historically lasted twelve to
                twenty-four months. Urgent operational issues with the
                Sentinel itself can be filed via{" "}
                <Link
                  to="/forum"
                  className="text-emerald-300 hover:text-emerald-100 underline-offset-4 hover:underline"
                >
                  Ask the Architect
                </Link>
                {" "}or the truth-ledger endpoints.
              </p>

              {submitted ? (
                <div
                  data-testid="architect-dead-letter-thanks"
                  className="border border-emerald-500/40 bg-emerald-500/[0.05] p-5 flex items-start gap-3"
                >
                  <CheckCircle2 className="h-5 w-5 text-emerald-300 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 mb-2">
                      Queued
                    </div>
                    <p className="text-sm text-zinc-300 leading-relaxed">
                      Your message is on the queue. Thanks for the patience.
                      The Sentinel keeps running.
                    </p>
                    <button
                      type="button"
                      onClick={() => setSubmitted(false)}
                      data-testid="architect-dead-letter-send-another"
                      className="mt-4 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-emerald-300"
                    >
                      Send another →
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                        Your name (optional)
                      </span>
                      <input
                        type="text"
                        value={form.from_name}
                        onChange={(e) =>
                          setForm({ ...form, from_name: e.target.value })
                        }
                        maxLength={100}
                        data-testid="architect-dead-letter-name"
                        className="w-full bg-transparent border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:border-emerald-500 focus:outline-none"
                      />
                    </label>
                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                        How to reach you (optional)
                      </span>
                      <input
                        type="text"
                        value={form.from_contact}
                        onChange={(e) =>
                          setForm({ ...form, from_contact: e.target.value })
                        }
                        maxLength={200}
                        placeholder="email · handle · anything"
                        data-testid="architect-dead-letter-contact"
                        className="w-full bg-transparent border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:border-emerald-500 focus:outline-none"
                      />
                    </label>
                  </div>
                  <label className="block">
                    <span className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                      Subject (optional)
                    </span>
                    <input
                      type="text"
                      value={form.subject}
                      onChange={(e) =>
                        setForm({ ...form, subject: e.target.value })
                      }
                      maxLength={200}
                      data-testid="architect-dead-letter-subject"
                      className="w-full bg-transparent border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:border-emerald-500 focus:outline-none"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                      Message *
                    </span>
                    <textarea
                      value={form.body}
                      onChange={(e) =>
                        setForm({ ...form, body: e.target.value })
                      }
                      maxLength={5000}
                      rows={6}
                      required
                      data-testid="architect-dead-letter-body"
                      className="w-full bg-transparent border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:border-emerald-500 focus:outline-none resize-y font-mono"
                    />
                    <span className="block mt-1 text-[10px] font-mono text-zinc-600">
                      {form.body.length} / 5000
                    </span>
                  </label>
                  <button
                    type="submit"
                    disabled={submitting || form.body.trim().length < 2}
                    data-testid="architect-dead-letter-submit"
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-emerald-500/60 text-[11px] uppercase tracking-[0.3em] text-emerald-300 hover:bg-emerald-500 hover:text-zinc-950 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {submitting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Send className="h-3.5 w-3.5" />
                    )}
                    Queue message
                  </button>
                </form>
              )}
            </section>

            {/* Design context */}
            <div className="mt-10 pt-8 border-t border-zinc-900 text-sm text-zinc-500 leading-relaxed">
              <p>
                This page exists because the Projectivy Sentinel was
                deliberately designed to run without its author. The Architect&rsquo;s
                pattern of withdrawing during high-visibility periods is not a
                failure mode; it is part of the sustainable-empathy discipline
                that makes the whole architecture load-bearing.
              </p>
              <p className="mt-3 font-mono text-xs text-zinc-600">
                GET /api/architect/status · POST /api/architect/dead-letter
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
